import api from 'api';

import { useMemo, useState } from 'react';
import { TableLayout } from 'lib/components/TableLayout';
import { Alert } from 'lib/components/Alert';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import {
  ElasticOrderSearchResults,
  SearchablePublicationIssueSectionRecord
} from 'lib/types/searchable';
import { ColumnService } from 'lib/services/directory';
import { ElasticOrdersAggregationParams } from 'lib/types/api';
import { getPublicationTimestampForElasticQuery } from 'lib/utils/dates';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ColumnButton } from 'lib/components/ColumnButton';
import Card from 'lib/components/Card/Card';
import { useReportsLoading } from '../context/LoadingContext';
import { useReportParams } from '../context/ReportParamsContext';
import { fetchPublicationIssueSectionsWithParams } from '../utils/elastic';
import { ReportHeader } from '../components/ReportHeader';
import PublicationIssuesTableFilterDialog, {
  PublicationIssuesTableFilter
} from './PublicationIssuesTableFilterDialog';

export function PaginationTab() {
  const { getOrgIds, getDateRange } = useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();
  const { withLoading } = useReportsLoading();

  // Fetch publication issue sections
  const {
    value: publicationIssueSections,
    isLoading: isLoadingSections,
    error: sectionsError
  } = useAsyncEffect({
    fetchData: () => {
      return fetchPublicationIssueSectionsWithParams(
        getOrgIds,
        getDateRange,
        withLoading
      );
    },
    initialData: [],
    dependencies: [getOrgIds().sort().join(','), dateRange, startDate, endDate],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS V2] Failed to fetch publication issue sections for Pagination tab',
      tags: {
        orgsFilter: getOrgIds().join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || ''
      }
    }
  });

  // Fetch orders aggregation data
  const {
    value: aggregationsData,
    isLoading: isLoadingOrders,
    error: ordersError
  } = useAsyncEffect<ElasticOrderSearchResults | null>({
    fetchData: async () => {
      if (!publicationIssueSections?.length) return null;

      const publishersDatesAndMediums: ElasticOrdersAggregationParams[] =
        publicationIssueSections.map(
          (section: SearchablePublicationIssueSectionRecord) => ({
            publisherId: section.publisherid,
            publicationDate: getPublicationTimestampForElasticQuery({
              dayString: section.publicationdate
            }),
            medium: section.publishingmedium
          })
        );

      const { response } = await api.safePost('search/orders-aggregation', {
        publishersDatesAndMediums
      });

      return response;
    },
    initialData: null,
    dependencies: [publicationIssueSections],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message: '[COLUMN REPORTS V2] Failed to fetch orders aggregation data',
      tags: {
        orgsFilter: getOrgIds().join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || ''
      }
    }
  });

  type Row = {
    id: string;
    sectionId: string;
    publicationissueid: string;
    publicationDate: string;
    newspaper: string;
    status: string;
    medium: string;
    product: string;
    filingType: string;
    ordersCount?: number;
    totalColumnInches?: number;
  };

  const rows: Row[] = useMemo(() => {
    const mappedRows: Row[] = [];

    (publicationIssueSections || []).forEach(
      (section: SearchablePublicationIssueSectionRecord) => {
        const publisherBucket = (
          aggregationsData?.aggregations?.publisherid as any
        )?.buckets?.find(
          (bucket: { key: string }) => bucket.key === section.publisherid
        );

        const timestampBucket =
          publisherBucket?.publishingtimestamp?.buckets?.find(
            (bucket: { key: number }) =>
              bucket.key ===
              getPublicationTimestampForElasticQuery({
                dayString: section.publicationdate
              })
          );

        const orderTypeBucket = timestampBucket?.ordertype?.buckets?.find(
          (bucket: { key: string }) => bucket.key === section.type
        );

        const mediumBucket = orderTypeBucket?.publishingmedium?.buckets?.find(
          (bucket: { key: string }) => bucket.key === section.publishingmedium
        );

        // If any bucket is missing, show a row with zero orders and N/A filing type (we want to show all order pagination sections, even empty ones)
        if (
          !publisherBucket ||
          !timestampBucket ||
          !orderTypeBucket ||
          !mediumBucket
        ) {
          mappedRows.push({
            id: section.id,
            sectionId: section.id,
            publicationissueid: section.publicationissueid,
            publicationDate: section.publicationdate,
            newspaper: section.publishername,
            status: section.status,
            medium: section.publishingmedium,
            product: section.type,
            filingType: '',
            ordersCount: 0,
            totalColumnInches: 0
          });
          return;
        }

        // Get filing type buckets from the medium bucket
        const filingTypeBuckets = mediumBucket?.filingtype?.buckets || [];

        if (filingTypeBuckets.length === 0) {
          // If no filing types, create a single row with empty filing type
          mappedRows.push({
            id: section.id,
            sectionId: section.id,
            publicationissueid: section.publicationissueid,
            publicationDate: section.publicationdate,
            newspaper: section.publishername,
            status: section.status,
            medium: section.publishingmedium,
            product: section.type,
            filingType: '',
            ordersCount: mediumBucket?.doc_count || 0,
            totalColumnInches: mediumBucket?.totalcolumninches?.value || 0
          });
        } else {
          // Create a row for each filing type
          filingTypeBuckets.forEach((filingTypeBucket: any) => {
            mappedRows.push({
              id: `${section.id}-${filingTypeBucket.key}`,
              sectionId: section.id,
              publicationissueid: section.publicationissueid,
              publicationDate: section.publicationdate,
              newspaper: section.publishername,
              status: section.status,
              medium: section.publishingmedium,
              product: section.type,
              filingType: filingTypeBucket.key,
              ordersCount: filingTypeBucket?.doc_count || 0,
              totalColumnInches: filingTypeBucket?.totalcolumninches?.value || 0
            });
          });
        }
      }
    );

    return mappedRows;
  }, [publicationIssueSections, aggregationsData]);

  const [filter, setFilter] = useState<PublicationIssuesTableFilter>({
    status: '',
    medium: '',
    product: ''
  });
  const [editedFilter, setEditedFilter] =
    useState<PublicationIssuesTableFilter>(filter);
  const [search, setSearch] = useState('');

  const filteredRows: Row[] = useMemo(() => {
    return rows.filter((row: Row) => {
      const matchesStatus = !filter.status || row.status === filter.status;
      const matchesMedium = !filter.medium || row.medium === filter.medium;
      const matchesProduct = !filter.product || row.product === filter.product;
      const matchesSearch =
        !search || row.newspaper.toLowerCase().includes(search.toLowerCase());
      return matchesStatus && matchesMedium && matchesProduct && matchesSearch;
    });
  }, [rows, filter, search]);

  const renderRow = (row: Row) => {
    const handleDownloadManifest = async () => {
      const { response, error } = await api.safePost(
        'pagination/download-orders-manifest',
        {
          publicationIssueId: row.publicationissueid,
          publicationIssueSectionId: row.sectionId,
          filingType: row.filingType
        }
      );

      if (error || !response?.url) {
        console.error('Failed to download manifest:', error);
        return;
      }

      window.open(response.url, '_blank');
    };

    return (
      <>
        <td>{row.publicationDate}</td>
        <td>{row.newspaper}</td>
        <td>{row.status}</td>
        <td>{row.medium}</td>
        <td>{row.product}</td>
        <td>{row.filingType || 'N/A'}</td>
        <td>{row.ordersCount}</td>
        <td>{row.totalColumnInches?.toFixed(2)}</td>
        <td>
          <ColumnButton
            tertiary
            size="sm"
            onClick={handleDownloadManifest}
            startIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
            type="button"
            buttonText="Download CSV"
            disabled={!row.ordersCount}
          />
        </td>
      </>
    );
  };

  return (
    <Card>
      <ReportHeader title="Pagination Issues" />
      <TableLayout
        loading={isLoadingSections || isLoadingOrders}
        data={filteredRows}
        columns={[
          'Publication Date',
          'Newspaper',
          'Status',
          'Medium',
          'Product',
          'Filing Type',
          'Orders',
          'Total Column Inches',
          'Download CSV'
        ]}
        filterable={{
          shouldShowTableItem: (item: Row, search: string) =>
            item.newspaper.toLowerCase().includes(search.toLowerCase()),
          searchEnabled: true,
          onSearch: setSearch,
          additionalFilters: {
            applyFilterChanges: () => setFilter(editedFilter),
            resetFilters: () => {
              setFilter({ status: '', medium: '', product: '' });
              setEditedFilter({ status: '', medium: '', product: '' });
            },
            filterHasChanges:
              JSON.stringify(filter) !== JSON.stringify(editedFilter),
            numFiltersActive: Object.values(filter).filter(Boolean).length,
            onDialogOpen: () => setEditedFilter(filter),
            renderDialog: () => (
              <PublicationIssuesTableFilterDialog
                filter={editedFilter}
                setFilter={setEditedFilter}
              />
            )
          }
        }}
        header={{
          title: '',
          subtitle: ''
        }}
        renderRow={renderRow}
      />
      {(sectionsError || ordersError) && (
        <Alert status="error" id="pagination-tab-error">
          Error fetching pagination issues:{' '}
          {sectionsError?.message || ordersError?.message}
        </Alert>
      )}
    </Card>
  );
}

export default PaginationTab;
